<template>
  <v-card class="my-4">
    <v-card-title>
      Emails
      <v-spacer />
      <v-btn
        text
        class="mr-2"
        :to="{ name: 'emailDetail', params: { key: 'layout' } }"
      >
        Header &amp; footer
      </v-btn>
      <v-menu
        offset-y
        left
      >
        <template #activator="{ on, attrs }">
          <v-btn
            icon
            class="mr-2"
            title="More"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click="importFromCustomerIo">
            <v-list-item-title>Import unsubscribes and bounces from Customer.io</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        color="primary"
        @click="openNew"
      >
        <v-icon left>
          mdi-plus
        </v-icon>
        New email
      </v-btn>
    </v-card-title>

    <v-card-text class="pb-0">
      <v-text-field
        v-model="search"
        label="Search emails"
        prepend-inner-icon="mdi-magnify"
        dense
        outlined
        clearable
        hide-details
        style="max-width: 320px;"
        class="mb-3"
      />
    </v-card-text>

    <v-data-table
      :headers="headers"
      :items="rows"
      :search="search"
      :loading="loading"
      :items-per-page="100"
      hide-default-footer
      class="row-pointer"
      no-data-text="No emails yet."
      no-results-text="No emails match your search."
      @click:row="open"
    >
      <template #[`item.name`]="{ item }">
        <span class="font-weight-medium">{{ item.name }}</span>
      </template>
      <template #[`item.status`]="{ item }">
        <v-chip
          v-if="!item.version"
          small
          color="warning"
          text-color="white"
        >
          Draft
        </v-chip>
        <v-chip
          v-else-if="item.hasDraft"
          small
          color="warning"
          outlined
        >
          Unpublished changes
        </v-chip>
      </template>
    </v-data-table>

    <v-dialog
      v-model="newDialog"
      max-width="520"
    >
      <v-card>
        <v-card-title>New email</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="create">
            <v-text-field
              v-model="form.name"
              label="Name"
              placeholder="Order delayed"
              outlined
              dense
              autofocus
            />
            <div class="text-body-2 font-weight-medium mb-2">
              Who gets it
            </div>
            <v-radio-group
              v-model="form.kind"
              class="mt-0"
            >
              <v-radio value="transactional">
                <template #label>
                  <div>
                    <div class="font-weight-medium">
                      One person, after something happens
                    </div>
                    <div class="text-caption">
                      Like a receipt or a password reset. A developer connects it to the event that sends it.
                    </div>
                  </div>
                </template>
              </v-radio>
              <v-radio
                value="automation"
                class="mt-2"
              >
                <template #label>
                  <div>
                    <div class="font-weight-medium">
                      A step in an automation
                    </div>
                    <div class="text-caption">
                      Marketing, with an unsubscribe link. People who unsubscribed don't get it.
                    </div>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
            <v-select
              v-model="form.copyFrom"
              :items="copyOptions"
              label="Start from"
              outlined
              dense
            />
            <v-alert
              v-if="formError"
              type="error"
              dense
              text
            >
              {{ formError }}
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="newDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="creating"
            @click="create"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'EmailsIndex',
  data: () => ({
    loading: false,
    search: '',
    newDialog: false,
    creating: false,
    formError: '',
    form: { name: '', kind: 'transactional', copyFrom: '' },
    headers: [
      { text: 'Email', value: 'name' },
      { text: 'Sent', value: 'sentWhen' },
      { text: '', value: 'status', sortable: false, align: 'end' }
    ]
  }),
  computed: {
    ...mapGetters({ list: 'emails/list' }),
    // The header & footer has its own button; the table is the emails themselves.
    rows() {
      return this.list.filter((x) => x.kind !== 'layout');
    },
    copyOptions() {
      return [{ text: 'A blank email', value: '' }].concat(
        this.rows.map((x) => ({ text: `A copy of "${x.name}"`, value: x.key }))
      );
    }
  },
  mounted() {
    this.loading = true;
    this.$store
      .dispatch('emails/getList')
      .catch((error) => this.$root.$snackbarError(this.errorText(error, 'Could not load the emails.')))
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    open(item) {
      this.$router.push({ name: 'emailDetail', params: { key: item.key } });
    },
    openNew() {
      this.form = { name: '', kind: 'transactional', copyFrom: '' };
      this.formError = '';
      this.newDialog = true;
    },
    create() {
      if (!this.form.name || !this.form.name.trim()) {
        this.formError = 'Give the email a name.';
        return;
      }
      this.creating = true;
      this.formError = '';
      this.$store
        .dispatch('emails/create', {
          name: this.form.name,
          kind: this.form.kind,
          copyFrom: this.form.copyFrom || null
        })
        .then((detail) => {
          this.newDialog = false;
          this.$router.push({ name: 'emailDetail', params: { key: detail.key } });
        })
        .catch((error) => {
          this.formError = this.errorText(error, 'The email was not created. Please try again.');
        })
        .finally(() => {
          this.creating = false;
        });
    },
    async importFromCustomerIo() {
      const go = await this.$root.$confirm(
        'Import from Customer.io?',
        'People who unsubscribed in Customer.io get marketing emails turned off, and bounced, complained and invalid addresses go on the do-not-email list. It runs in the background and emails you the result. Running it again only adds what is missing.',
        { color: 'primary' }
      );
      if (!go) return;
      this.$store
        .dispatch('emails/importFromCustomerIo')
        .then((result) => this.$root.$snackbarSuccess(`Import started. The result will be emailed to ${result.email}.`))
        .catch((error) => this.$root.$snackbarError(this.errorText(error, 'The import did not start.')));
    },
    errorText(error, fallback) {
      const data = error && error.response && error.response.data;
      if (typeof data === 'string') return data;
      return (data && data.message) || fallback;
    }
  }
};
</script>
